// The rules of a point based model, edited in place and saved as they change.
//
// Replaces, behind studio-tree-insights, the page that edited them in an
// accordion of forms with one Save for everything. Its shape is a reaction to
// the rebuilt Signals page, not a copy of it: on a customer call (2026-09-23)
// the customer could not tell there whether an edit applied, whether another row
// could be opened, or whether to press Cancel or Delete. So there is no Save,
// no Cancel and no open state here. Every control is the rule itself,
// and the page writes the document shortly after the last change, the way Fine
// tuning writes thresholds.
//
// A score is the sum, over groups, of the first rule in each group that matches.
// That sentence is the whole model, and the page draws it: groups stacked, rules
// numbered in the order they are tried, and each group's reach beside it.
import { closestCenter, DndContext, DragEndEvent } from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { inject, observer } from 'mobx-react';
import React from 'react';
import { Button, Form } from 'react-bootstrap';
import { Prompt as RouterPrompt } from 'react-router-dom';

import PointBasedConditionDataModel from '../../../models/pointBasedForm/PointBasedConditionDataModel';
import PointBasedRuleDataModel from '../../../models/pointBasedForm/PointBasedRuleDataModel';
import {
  pointsRange,
  signedPoints,
} from '../../../models/pointBasedForm/pointsRange';
import Store from '../../../store';
import Error from '../../components/error/Error';
import DraftNumberInput from '../../components/model/DraftNumberInput';
import PointRuleCondition from '../../components/rules/PointRuleCondition';
import SortableItem, {
  DragHandleProps,
} from '../../components/rules/SortableItem';
import {
  changedElsewhere,
  groupLabel,
  groupRange,
  holdReason,
  isRuleUnfinished,
  keyed,
  moveRule,
  newCondition,
  newRule,
  PointRuleGroup,
  renumber,
  unfinishedRules,
  withKeys,
  withoutKeys,
} from '../../components/rules/pointRules';
import {
  fetchPointRules,
  savePointRules,
} from '../../components/rules/pointRulesApi';
import Spinner from '../../components/Spinner';
import './rulesPage.css';
import {
  onRuleGroupFocus,
  onRulesRefresh,
} from '../../components/model/modelChangeBus';

/**
 * How long after the last change the rules are written.
 *
 * Longer than Fine tuning's 600ms on purpose: a rule's values are typed a
 * character at a time, and every write re-scores the training set to validate
 * the rules, so a shorter pause writes several times per word.
 */
const SAVE_DELAY_MS = 800;

const range = ({ min, max }: { min: number; max: number }) =>
  `${signedPoints(min)} to ${signedPoints(max)}`;

/**
 * The write still in flight per model, so a remount reads after it lands.
 *
 * Leaving the page inside the delay writes on unmount, and that write spends
 * seconds validating on the server. Coming straight back read the rules from
 * before it, and the next autosave wrote that old document back over it.
 */
const pendingSaves = new Map<string, Promise<void>>();

/** A group's name, committed on blur or Enter rather than on every pause. */
function GroupName({
  value,
  placeholder,
  disabled,
  onCommit,
}: {
  value: string;
  placeholder: string;
  disabled: boolean;
  onCommit: (name: string) => void;
}) {
  // Every write runs the whole document through the scoring SQL, so a rename
  // saved per pause in typing paid a full validation for each one.
  const [draft, setDraft] = React.useState<string | null>(null);
  const commit = () => {
    if (draft !== null && draft !== value) onCommit(draft);
    setDraft(null);
  };
  return (
    <Form.Control
      className="rp-group-name"
      aria-label="Group name"
      data-testid="rules-group-name"
      placeholder={placeholder}
      disabled={disabled}
      value={draft ?? value}
      onChange={(event) => setDraft(event.target.value)}
      onBlur={commit}
      onKeyDown={(event) => {
        if (event.key === 'Enter') (event.target as HTMLInputElement).blur();
      }}
    />
  );
}

interface RulesPageState {
  groups: PointRuleGroup[] | null;
  loadError: string | null;
  saving: boolean;
  saveError: string | null;
  /** A change the server has not seen yet, held or pending. */
  dirty: boolean;
  /**
   * The last group removed, so an autosaved delete can be taken back. Cleared
   * by the next edit: its index means nothing once the list has moved on.
   */
  removed: { group: PointRuleGroup; index: number } | null;
  /**
   * The server's rules changed since this page last read or wrote them,
   * because an agent or another tab wrote them. Autosave stops rather than
   * overwrite that with this page's older copy.
   */
  conflict: boolean;
  /**
   * The groups shown open, by key. A view setting, never saved: groups arrive
   * folded so a model reads as its list of groups, and a new group opens.
   */
  open: string[];
}

/** The drag handle, the one place a drag starts. */
function Grip({ handle, label }: { handle: DragHandleProps; label: string }) {
  return (
    <button type="button" className="rp-grip" aria-label={label} {...handle}>
      <i aria-hidden className="fas fa-grip-vertical" />
    </button>
  );
}

const RulesPage = inject('store')(
  observer(
    class RulesPage extends React.Component<{ store?: Store }, RulesPageState> {
      mounted = false;

      saveTimer: ReturnType<typeof setTimeout> | null = null;

      saveInFlight = false;

      saveAgain = false;

      /** Bumped on every edit; the page is saved when the stored revision matches. */
      revision = 0;

      savedRevision = 0;

      /**
       * The rules as the server held them when this page last read or wrote
       * them, to tell another writer's change from this page's own.
       */
      synced: PointRuleGroup[] | null = null;

      constructor(props: { store?: Store }) {
        super(props);
        this.state = {
          groups: null,
          loadError: null,
          saving: false,
          saveError: null,
          dirty: false,
          removed: null,
          conflict: false,
          open: [],
        };
      }

      warnOnLeave = (event: BeforeUnloadEvent) => {
        if (!this.state.dirty) return;
        event.preventDefault();
        // eslint-disable-next-line no-param-reassign
        event.returnValue = '';
      };

      saveKey() {
        const { tenant, modelId } = this.props.store;
        return `${tenant}/${modelId}`;
      }

      async componentDidMount() {
        this.mounted = true;
        window.addEventListener('beforeunload', this.warnOnLeave);
        // The agent wrote the rules. Take them, unless this page holds an edit of its
        // own: then the autosave's own check finds the server moved and says so,
        // rather than this silently dropping what the person typed.
        this.unsubscribeRulesRefresh = onRulesRefresh(() => {
          if (!this.state.dirty) this.reload().catch(() => undefined);
        });
        // A group the chat linked to: open it and bring it into view, once the groups
        // are here to find it among.
        this.unsubscribeGroupFocus = onRuleGroupFocus((group) => {
          this.wantedGroup = group;
          this.showWantedGroup();
        });
        const { store } = this.props;
        try {
          await pendingSaves.get(this.saveKey());
          const [groups] = await Promise.all([
            fetchPointRules(store.tenant, store.modelId),
            store.fetchFormFields(),
          ]);
          this.synced = groups;
          if (this.mounted) {
            this.setState({ groups: withKeys(groups) }, () =>
              this.showWantedGroup()
            );
          }
        } catch (error) {
          if (this.mounted) {
            this.setState({
              loadError: "This model's rules could not be loaded.",
            });
          }
        }
      }

      unsubscribeRulesRefresh?: () => void;

      unsubscribeGroupFocus?: () => void;

      /** The group the chat asked for, until the groups have loaded to show it. */
      wantedGroup: number | null = null;

      showWantedGroup() {
        const group = this.wantedGroup;
        const target = group ? this.state.groups[group - 1] : undefined;
        if (!group || !target) return;
        this.wantedGroup = null;
        if (!this.state.open.includes(target._key)) {
          this.setState({ open: [...this.state.open, target._key] });
        }
        requestAnimationFrame(() => {
          document
            .querySelector(`[data-anchor="rule-group-${group}"]`)
            ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        });
      }

      componentWillUnmount() {
        this.mounted = false;
        this.unsubscribeRulesRefresh?.();
        this.unsubscribeGroupFocus?.();
        window.removeEventListener('beforeunload', this.warnOnLeave);
        // Leaving with anything unsaved, whether inside the delay or after a
        // failed write, writes it now rather than never. An unfinished rule
        // still holds it: writing that would fail and roll back the finished
        // ones with it.
        if (this.saveTimer) {
          clearTimeout(this.saveTimer);
          this.saveTimer = null;
        }
        if (this.state.dirty) void this.persist();
      }

      /** Replace the document and schedule the write. */
      update(
        groups: PointRuleGroup[],
        removed: RulesPageState['removed'] = null
      ) {
        this.revision += 1;
        this.setState({ groups, dirty: true, removed, saveError: null });
        if (this.saveTimer) clearTimeout(this.saveTimer);
        this.saveTimer = setTimeout(() => {
          this.saveTimer = null;
          void this.persist();
        }, SAVE_DELAY_MS);
      }

      /**
       * Write the current document, once at a time.
       *
       * A change arriving mid-flight sets `saveAgain` rather than starting a
       * second request, and the next pass reads the document as it is then. Two
       * concurrent writes each send everything and the slower one wins, which is
       * how autosave silently reverts an edit.
       */
      async persist() {
        if (this.saveInFlight) {
          this.saveAgain = true;
          return;
        }
        this.saveInFlight = true;
        const key = this.saveKey();
        const run = this.writeUntilCurrent();
        pendingSaves.set(key, run);
        try {
          await run;
        } finally {
          this.saveInFlight = false;
          if (pendingSaves.get(key) === run) pendingSaves.delete(key);
        }
      }

      async writeUntilCurrent() {
        do {
          this.saveAgain = false;
          const { groups } = this.state;
          if (!groups || holdReason(groups) || this.state.conflict) break;
          const { revision } = this;
          if (this.mounted) this.setState({ saving: true });
          const { store } = this.props;
          // Every write sends the whole document, so checked just before it:
          // anything written elsewhere since this page read the rules would be
          // silently reverted. Narrow rather than airtight, a write landing
          // between this read and the PUT still loses. A failed read writes
          // anyway, as before this check existed.
          // eslint-disable-next-line no-await-in-loop
          const current = await fetchPointRules(
            store.tenant,
            store.modelId
          ).catch(() => null);
          if (changedElsewhere(current, this.synced)) {
            if (this.mounted) this.setState({ saving: false, conflict: true });
            break;
          }
          // A group with no rules is a CASE with no WHEN, which is not SQL,
          // so it is left out of what is written rather than failing it.
          const written = withoutKeys(
            renumber(groups.filter((group) => group.rules?.length))
          );
          // eslint-disable-next-line no-await-in-loop
          const error = await savePointRules(
            store.tenant,
            store.modelId,
            written
          );
          if (!error) {
            this.savedRevision = revision;
            this.synced = written;
            // The store's copy is read once per tab and never again, so the
            // unflagged page would show, and save, the rules from before this.
            store.invalidatePointBasedRules();
          }
          if (this.mounted) {
            this.setState({
              saving: false,
              saveError: error,
              // Saved only when what is stored is the page as it is now. An
              // edit still waiting on its timer is not that, whether or not a
              // write was in flight when it was made.
              dirty: Boolean(error) || this.revision !== this.savedRevision,
            });
          }
        } while (this.saveAgain);
      }

      changeGroup(index: number, change: Partial<PointRuleGroup>) {
        this.update(
          this.state.groups.map((group, at) =>
            at === index ? { ...group, ...change } : group
          )
        );
      }

      changeRule(
        groupIndex: number,
        ruleIndex: number,
        change: Partial<PointBasedRuleDataModel>
      ) {
        const group = this.state.groups[groupIndex];
        this.changeGroup(groupIndex, {
          rules: group.rules.map((rule, at) =>
            at === ruleIndex ? { ...rule, ...change } : rule
          ),
        });
      }

      changeCondition(
        groupIndex: number,
        ruleIndex: number,
        conditionIndex: number,
        condition: PointBasedConditionDataModel
      ) {
        const rule = this.state.groups[groupIndex].rules[ruleIndex];
        this.changeRule(groupIndex, ruleIndex, {
          conditions: rule.conditions.map((each, at) =>
            at === conditionIndex ? condition : each
          ),
        });
      }

      removeCondition(
        groupIndex: number,
        ruleIndex: number,
        conditionIndex: number
      ) {
        const rule = this.state.groups[groupIndex].rules[ruleIndex];
        this.changeRule(groupIndex, ruleIndex, {
          conditions: rule.conditions.filter((_, at) => at !== conditionIndex),
        });
      }

      /**
       * The last rule takes its group with it, as on the unflagged page: a
       * group with no rules is not written, so leaving it on screen showed a
       * group that was gone on the next load. Undo brings both back.
       */
      removeRule(groupIndex: number, ruleIndex: number) {
        const group = this.state.groups[groupIndex];
        if (group.rules.length === 1) {
          this.removeGroup(groupIndex);
          return;
        }
        this.changeGroup(groupIndex, {
          rules: group.rules.filter((_, at) => at !== ruleIndex),
        });
      }

      /** Take the server's rules, dropping this page's unsaved copy. */
      async reload() {
        const { store } = this.props;
        const groups = await fetchPointRules(store.tenant, store.modelId);
        this.synced = groups;
        this.revision += 1;
        this.savedRevision = this.revision;
        if (this.mounted) {
          this.setState({
            groups: withKeys(groups),
            conflict: false,
            dirty: false,
            removed: null,
            saveError: null,
          });
        }
      }

      /** The field a new rule or condition starts on: the first standard one. */
      firstField() {
        const { formFields } = this.props.store;
        return (
          formFields?.find((field) => field.context === 'standard') ??
          formFields?.[0]
        );
      }

      removeGroup(index: number) {
        const { groups } = this.state;
        this.update(
          groups.filter((_, at) => at !== index),
          { group: groups[index], index }
        );
      }

      undoRemove() {
        const { groups, removed } = this.state;
        if (!removed) return;
        const next = groups.slice();
        next.splice(Math.min(removed.index, next.length), 0, removed.group);
        this.update(next, null);
      }

      renderRule(group: PointRuleGroup, groupIndex: number, ruleIndex: number) {
        const { formFields, activeModel, isAllowedToEdit } = this.props.store;
        const rule = group.rules[ruleIndex];
        const disabled = !isAllowedToEdit;
        return (
          <SortableItem
            as="li"
            className="rp-rule"
            key={rule._key}
            id={rule._key}
            disabled={disabled}
          >
            {(handle) => (
              <>
                {!disabled && (
                  <Grip handle={handle} label="Drag to reorder this rule" />
                )}
                <span className="rp-rule-num" aria-hidden>
                  {ruleIndex + 1}
                </span>
                <div className="rp-rule-conditions">
                  {rule.conditions.map((condition, conditionIndex) => (
                    <PointRuleCondition
                      // eslint-disable-next-line react/no-array-index-key
                      key={conditionIndex}
                      condition={condition}
                      formFields={formFields ?? []}
                      modelType={activeModel.type}
                      disabled={disabled}
                      first={conditionIndex === 0}
                      onChange={(next) =>
                        this.changeCondition(
                          groupIndex,
                          ruleIndex,
                          conditionIndex,
                          next
                        )
                      }
                      onRemove={
                        rule.conditions.length > 1
                          ? () =>
                              this.removeCondition(
                                groupIndex,
                                ruleIndex,
                                conditionIndex
                              )
                          : undefined
                      }
                    />
                  ))}
                  <Button
                    size="sm"
                    variant="link"
                    className="rp-and"
                    disabled={disabled}
                    onClick={() =>
                      this.changeRule(groupIndex, ruleIndex, {
                        conditions: [
                          ...rule.conditions,
                          newCondition(this.firstField()),
                        ],
                      })
                    }
                  >
                    + and
                  </Button>
                </div>
                <label className="rp-points">
                  <DraftNumberInput
                    value={rule.points}
                    disabled={disabled}
                    aria-label={`Points for rule ${ruleIndex + 1}`}
                    data-testid="rules-rule-points"
                    onCommit={(points) =>
                      this.changeRule(groupIndex, ruleIndex, { points })
                    }
                  />
                  <span>pts</span>
                </label>
                <span className="rp-rule-tools">
                  <button
                    type="button"
                    className="rp-icon"
                    aria-label="Try this rule earlier"
                    disabled={disabled || ruleIndex === 0}
                    onClick={() =>
                      this.update(
                        moveRule(this.state.groups, groupIndex, ruleIndex, -1)
                      )
                    }
                  >
                    <i aria-hidden className="fas fa-arrow-up" />
                  </button>
                  <button
                    type="button"
                    className="rp-icon"
                    aria-label="Try this rule later"
                    disabled={disabled || ruleIndex === group.rules.length - 1}
                    onClick={() =>
                      this.update(
                        moveRule(this.state.groups, groupIndex, ruleIndex, 1)
                      )
                    }
                  >
                    <i aria-hidden className="fas fa-arrow-down" />
                  </button>
                  <button
                    type="button"
                    className="rp-icon rp-icon--danger"
                    aria-label="Remove this rule"
                    disabled={disabled}
                    onClick={() => this.removeRule(groupIndex, ruleIndex)}
                  >
                    <i aria-hidden className="fas fa-times" />
                  </button>
                </span>
              </>
            )}
          </SortableItem>
        );
      }

      /** Reorder within one list, by key, as dnd-kit reports a drop. */
      reorder<T extends { _key?: string }>(
        items: T[],
        { active, over }: DragEndEvent
      ): T[] | null {
        if (!over || active.id === over.id) return null;
        const from = items.findIndex((item) => item._key === active.id);
        const to = items.findIndex((item) => item._key === over.id);
        if (from < 0 || to < 0) return null;
        return arrayMove(items, from, to);
      }

      toggle(key: string) {
        const { open } = this.state;
        this.setState({
          open: open.includes(key)
            ? open.filter((each) => each !== key)
            : [...open, key],
        });
      }

      renderGroup(group: PointRuleGroup, index: number) {
        const { isAllowedToEdit } = this.props.store;
        const disabled = !isAllowedToEdit;
        // A group holding an unfinished rule is always open: the warning names
        // that rule, and folding it away would hide the thing being asked for.
        const holding = group.rules.some(isRuleUnfinished);
        const isOpen = holding || this.state.open.includes(group._key);
        return (
          <SortableItem
            as="section"
            className="rp-group"
            key={group._key}
            id={group._key}
            anchor={`rule-group-${index + 1}`}
            disabled={disabled}
          >
            {(handle) => (
              <>
                <header className="rp-group-head">
                  {!disabled && (
                    <Grip handle={handle} label="Drag to reorder this group" />
                  )}
                  <button
                    type="button"
                    className="rp-icon rp-fold"
                    aria-expanded={isOpen}
                    aria-label={isOpen ? 'Fold this group' : 'Open this group'}
                    disabled={holding}
                    onClick={() => this.toggle(group._key)}
                  >
                    <i
                      aria-hidden
                      className={
                        isOpen ? 'fas fa-chevron-down' : 'fas fa-chevron-right'
                      }
                    />
                  </button>
                  {/* The heading IS the name. Empty, it shows what the group is
                  shown as, so a group is never unnamed on screen. */}
                  <GroupName
                    placeholder={groupLabel({ ...group, name: '' }, index)}
                    disabled={disabled}
                    value={group.name ?? ''}
                    onCommit={(name) => this.changeGroup(index, { name })}
                  />
                  <span className="rp-group-count">
                    {group.rules.length}{' '}
                    {group.rules.length === 1 ? 'rule' : 'rules'}
                  </span>
                  <span
                    className="rp-group-range"
                    title="Points this group can add"
                  >
                    {range(groupRange(group))}
                  </span>
                  <button
                    type="button"
                    className="rp-icon rp-icon--danger rp-group-remove"
                    aria-label={`Remove ${groupLabel(group, index)}`}
                    disabled={disabled}
                    onClick={() => this.removeGroup(index)}
                  >
                    <i aria-hidden className="fas fa-trash" />
                  </button>
                </header>
                {isOpen && (
                  <>
                    {group.rules.length ? (
                      <DndContext
                        collisionDetection={closestCenter}
                        onDragEnd={(event) => {
                          const rules = this.reorder(group.rules, event);
                          if (rules) this.changeGroup(index, { rules });
                        }}
                      >
                        <SortableContext
                          items={group.rules.map((rule) => rule._key)}
                          strategy={verticalListSortingStrategy}
                        >
                          <ol className="rp-rules">
                            {group.rules.map((_, ruleIndex) =>
                              this.renderRule(group, index, ruleIndex)
                            )}
                          </ol>
                        </SortableContext>
                      </DndContext>
                    ) : (
                      <p className="rp-empty">No rules</p>
                    )}
                    <Button
                      size="sm"
                      variant="link"
                      className="rp-add"
                      data-testid="rules-add-rule"
                      disabled={disabled}
                      onClick={() =>
                        this.changeGroup(index, {
                          rules: [
                            ...group.rules,
                            keyed(newRule(this.firstField())),
                          ],
                        })
                      }
                    >
                      + Add rule
                    </Button>
                  </>
                )}
              </>
            )}
          </SortableItem>
        );
      }

      render() {
        const { isAllowedToEdit } = this.props.store;
        const {
          groups,
          loadError,
          saving,
          saveError,
          dirty,
          removed,
          conflict,
        } = this.state;
        if (loadError) return <Error message={loadError} />;
        if (!groups) return <Spinner />;

        const held = unfinishedRules(groups);
        const empty = holdReason(groups) === 'empty';
        const allOpen = groups.every(
          (group) =>
            this.state.open.includes(group._key) ||
            group.rules.some(isRuleUnfinished)
        );
        const heldText = held
          .map(
            ({ group, rule }) =>
              `rule ${rule + 1} in ${groupLabel(groups[group], group)}`
          )
          .join(', ');

        return (
          <div className="rp mt-3 mb-5">
            {/* An unsaved change that leaving would lose: one held back, one whose
                write failed, or one stopped by another writer. A change still
                inside the delay is written on unmount instead. */}
            <RouterPrompt
              when={
                dirty &&
                (held.length > 0 || empty || Boolean(saveError) || conflict)
              }
              message="Your latest changes to these rules are not saved. Leave anyway?"
            />
            <div className="rp-head">
              <h5 className="rp-title">Rules</h5>
              <span className="rp-total" title="The totals a lead can reach">
                {range(pointsRange(groups))}
              </span>
              <Button
                size="sm"
                variant="link"
                className="rp-fold-all"
                onClick={() =>
                  this.setState({
                    open: allOpen ? [] : groups.map((group) => group._key),
                  })
                }
              >
                {allOpen ? 'Fold all' : 'Open all'}
              </Button>
              <span
                data-testid="rules-save-state"
                className={`rp-state ${
                  saving || dirty ? '' : 'rp-state--saved'
                }`}
              >
                {/* Held or pending both read as not saved; a held change also
                    has the warning below saying why. */}
                {saving ? 'Saving...' : dirty ? '' : 'Saved'}
              </span>
            </div>

            {held.length > 0 && (
              <p className="rp-warn" data-testid="rules-held">
                Finish {heldText} to save.
              </p>
            )}
            {empty && (
              <p className="rp-warn" data-testid="rules-empty">
                Add a rule to save. A rule based model needs at least one.
              </p>
            )}
            {saveError && <Error message={saveError} />}
            {conflict && (
              <p className="rp-warn" data-testid="rules-conflict">
                These rules were changed elsewhere since this page opened, so
                your latest edits are not saved.{' '}
                <Button size="sm" variant="link" onClick={() => this.reload()}>
                  Load the current rules
                </Button>
              </p>
            )}
            {removed && (
              <p className="rp-undo">
                {groupLabel(removed.group, removed.index)} removed.{' '}
                <Button
                  size="sm"
                  variant="link"
                  onClick={() => this.undoRemove()}
                >
                  Undo
                </Button>
              </p>
            )}

            <DndContext
              collisionDetection={closestCenter}
              onDragEnd={(event) => {
                const next = this.reorder(groups, event);
                if (next) this.update(next);
              }}
            >
              <SortableContext
                items={groups.map((group) => group._key)}
                strategy={verticalListSortingStrategy}
              >
                {groups.map((group, index) => this.renderGroup(group, index))}
              </SortableContext>
            </DndContext>

            {isAllowedToEdit && (
              <Button
                size="sm"
                variant="outline-primary"
                className="rp-add-group"
                data-testid="rules-add-group"
                onClick={() => {
                  const group = keyed({
                    number: groups.length + 1,
                    rules: [keyed(newRule(this.firstField()))],
                  });
                  this.update([...groups, group]);
                  this.setState({ open: [...this.state.open, group._key] });
                }}
              >
                + Add rule group
              </Button>
            )}
          </div>
        );
      }
    }
  )
);

export default RulesPage;
